import type { ExpenseWithDetails } from '@/api/expenses';

export type ExpenseShareSource = Pick<ExpenseWithDetails, 'paid_by' | 'splits'>;

export interface UserExpenseShare {
  kind: 'lent' | 'owes';
  /** Amount in the expense's source currency minor units (always > 0). */
  amountMinor: number;
}

/**
 * What a single expense means for `userId`: the amount they lent as the payer,
 * the amount they owe as a participant, or null when neither applies.
 */
export function getUserExpenseShare(
  expense: ExpenseShareSource,
  userId: string | undefined
): UserExpenseShare | null {
  if (!userId) return null;

  const splits = expense.splits ?? [];

  if (expense.paid_by === userId) {
    // Sum the other participants' splits instead of "total - own split": a payer
    // who is not a participant has no split row of their own.
    const lentMinor = splits
      .filter((split) => split.user_id !== userId)
      .reduce((sum, split) => sum + Number(split.amount_minor), 0);
    return lentMinor > 0 ? { kind: 'lent', amountMinor: lentMinor } : null;
  }

  const ownSplit = splits.find((split) => split.user_id === userId);
  const owedMinor = ownSplit ? Number(ownSplit.amount_minor) : 0;
  return owedMinor > 0 ? { kind: 'owes', amountMinor: owedMinor } : null;
}
